import React from 'react';
import { Box, Typography, TextField } from '@mui/material';
import ReadOnly from './ReadOnly';
import { NODE_TYPES } from '../../constants';

const TIMEOUT_KEYS = ['timeout', 'timeoutMessage'];

function PropertiesTab({
  attributes,
  onAttributeChange,
  nodeType,
  routes,
  readOnly,
}) {
  const hasIncomingMessageRoute =
    routes?.some(r => r.signal === 'INCOMING_MESSAGE') ?? false;
  const showTimeoutSettings =
    (nodeType === NODE_TYPES.SEND_MESSAGE ||
      nodeType === NODE_TYPES.SEND_MESSAGE_AND_WAIT) &&
    hasIncomingMessageRoute;

  const stringKeys =
    attributes &&
    Object.keys(attributes).filter(
      key =>
        typeof attributes[key] === 'string' &&
        (!showTimeoutSettings || !TIMEOUT_KEYS.includes(key)),
    );
  const hasGenericProps = stringKeys?.length > 0;
  const hasAnyProps =
    hasGenericProps ||
    showTimeoutSettings ||
    (attributes &&
      Object.keys(attributes).some(k => typeof attributes[k] === 'number'));

  if (!attributes || (!hasAnyProps && Object.keys(attributes).length === 0)) {
    return (
      <Typography
        variant="body2"
        color="text.disabled"
        fontStyle="italic"
        align="center"
        sx={{ py: 4 }}>
        No configurable attributes for this node type.
      </Typography>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={2} pt={1}>
      <Typography
        variant="overline"
        color="text.disabled"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          pb: 0.5,
          fontWeight: 'bold',
        }}>
        Properties
      </Typography>
      {stringKeys?.map(key => {
        const isTextArea = ['body', 'input', 'prompt'].includes(key);
        return readOnly ? (
          <ReadOnly name={key} value={attributes[key]} />
        ) : (
          <TextField
            key={key}
            fullWidth
            label={key
              .replace(/([A-Z])/g, ' $1')
              .trim()
              .replace(/^\w/, c => c.toUpperCase())}
            variant="outlined"
            size="large"
            multiline={isTextArea}
            rows={isTextArea ? 5 : 1}
            value={attributes[key]}
            onChange={e => onAttributeChange(key, e.target.value)}
            InputProps={{
              sx: {
                bgcolor: 'inputBackground',
                fontFamily: isTextArea ? 'monospace' : 'inherit',
              },
            }}
          />
        );
      })}
      {showTimeoutSettings &&
        (readOnly ? (
          <ReadOnly name="timeout" value={attributes.timeout ?? 3600} />
        ) : (
          <TextField
            fullWidth
            type="number"
            label="Timeout (seconds)"
            variant="outlined"
            size="large"
            value={attributes.timeout ?? 3600}
            onChange={e => {
              const v =
                e.target.value === '' ? undefined : Number(e.target.value);
              onAttributeChange('timeout', v);
            }}
            placeholder="3600"
            helperText="How long to wait for a reply before following the Timeout path (seconds). Only used if the Timeout output is connected."
            InputProps={{
              sx: { bgcolor: 'inputBackground' },
              inputProps: { min: 1, step: 1 },
            }}
          />
        ))}
    </Box>
  );
}

export default PropertiesTab;
